import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  ArrowBackRounded as BackIcon,
  CalendarMonthRounded as CalendarIcon,
  ContactPhoneRounded as ContactIcon,
  EditRounded as EditIcon,
  GridViewRounded as ManageIcon,
  HeightRounded as HeightIcon,
  Inventory2Outlined as ContainerIcon,
  PrecisionManufacturingRounded as CraneIcon,
  ScaleRounded as WeightIcon,
  TagRounded as TeuIcon,
  ViewModule as StackerIcon,
} from "@mui/icons-material";
import MobileVesselPositionGrid from "./MobileVesselPositionGrid";

type MobileContainerDetailsDialogProps = {
  open: boolean;
  container: any;
  vessel?: any;
  terminalCall?: any;
  terminalCalls?: any[];
  terminals?: any[];
  direction?: "loading" | "unloading";
  onClose: () => void;
  onManage?: () => void;
};

const getId = (value: any) => {
  const source = typeof value === "string" ? value : value?.["@id"] || value?.id;
  return source ? String(source).split("/").filter(Boolean).pop() : "";
};

const terminalName = (terminalCall: any) =>
  terminalCall?.terminal?.name ||
  terminalCall?.terminal?.abbreviation ||
  "—";

const terminalContact = (terminalCall: any) => {
  const contacts = Array.isArray(terminalCall?.terminal?.contacts)
    ? terminalCall.terminal.contacts
    : [];
  const contact = contacts[0] || {};

  return [contact.name, contact.phoneNumber, contact.email]
    .filter(Boolean)
    .join(" · ") || "—";
};

const terminalDate = (terminalCall: any) => {
  const value = terminalCall?.planned || terminalCall?.arrival;
  return value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—";
};

const MobileContainerDetailsDialog: React.FC<MobileContainerDetailsDialogProps> = ({
  open,
  container,
  vessel,
  terminalCall,
  terminalCalls = [],
  terminals = [],
  direction = "loading",
  onClose,
  onManage,
}) => {
  const { t } = useTranslation();
  const type = container?.containerType;
  const position = Array.isArray(container?.position) ? container.position : [];
  const resolveTerminalCall = (direction: "loading" | "unloading") => {
    const reference = container?.[`${direction}TerminalCall`];
    const referenceId = getId(reference);
    const matchingCall = [terminalCall, ...terminalCalls].find(
      (call) => referenceId && getId(call) === referenceId
    );
    const resolvedCall =
      matchingCall ||
      (reference && typeof reference === "object" ? reference : {});
    const terminalReference = resolvedCall?.terminal;
    const resolvedTerminal =
      terminals.find(
        (terminalOption) =>
          getId(terminalOption) &&
          getId(terminalOption) === getId(terminalReference)
      ) ||
      (terminalReference && typeof terminalReference === "object"
        ? terminalReference
        : {});

    return {
      ...resolvedCall,
      terminal: resolvedTerminal,
    };
  };
  const loadingTerminalCall = resolveTerminalCall("loading");
  const unloadingTerminalCall = resolveTerminalCall("unloading");

  return (
    <Dialog open={open} onClose={onClose} fullScreen className="mobile-sheet-dialog">
      <AppBar position="sticky" elevation={0} className="mobile-detail-appbar">
        <Toolbar className="mobile-detail-toolbar">
          <IconButton color="inherit" onClick={onClose} aria-label={t("back", "Back")}><BackIcon /></IconButton>
          <Box className="mobile-detail-title">
            <Typography>{t("containerDetails", "Container details")}</Typography>
            <span>{container?.uid || "—"}</span>
          </Box>
          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      <Box className="mobile-container-detail-page">
        <Box className="mobile-container-hero">
          <span className="mobile-container-illustration"><StackerIcon /></span>
          <Box>
            <span className="mobile-container-type-chip">{type?.abbreviation || type?.name || "Container"}</span>
            <Typography component="h2">{container?.uid || "—"}</Typography>
            <span className={`mobile-onboard-chip${container?.loadedDate ? " is-onboard" : ""}`}>
              {container?.loadedDate ? t("alreadyOnBoard", "Already on board") : t("planned", "Planned")}
            </span>
          </Box>
        </Box>

        <Box className="mobile-container-stats">
          <div><WeightIcon /><span>{t("weight", "Weight")}</span><strong>{((Number(container?.weight) || 0) / 1000).toFixed(3)} T</strong></div>
          <div><HeightIcon /><span>{t("height", "Height")}</span><strong>{type?.height || "2.59 m"}</strong></div>
          <div><TeuIcon /><span>TEU</span><strong>{type?.teu || 0}</strong></div>
          <div><ContainerIcon /><span>{t("type", "Type")}</span><strong>{type?.abbreviation || type?.name || "—"}</strong></div>
        </Box>

        <Box className="mobile-container-route-card is-loading">
          <span className="mobile-route-icon"><CraneIcon /></span>
          <Box><span>{t("loadingTerminal", "Loading terminal")}</span><strong>{terminalName(loadingTerminalCall)}</strong></Box>
          <Box className="mobile-route-meta">
            <span className="mobile-route-date"><CalendarIcon />{terminalDate(loadingTerminalCall)}</span>
            <span className="mobile-route-contact"><ContactIcon />{terminalContact(loadingTerminalCall)}</span>
          </Box>
        </Box>

        <Box className="mobile-container-route-card is-unloading">
          <span className="mobile-route-icon"><CraneIcon /></span>
          <Box><span>{t("unloadingTerminal", "Unloading terminal")}</span><strong>{terminalName(unloadingTerminalCall)}</strong></Box>
          <Box className="mobile-route-meta">
            <span className="mobile-route-date"><CalendarIcon />{terminalDate(unloadingTerminalCall)}</span>
            <span className="mobile-route-contact"><ContactIcon />{terminalContact(unloadingTerminalCall)}</span>
          </Box>
        </Box>

        <Box className="mobile-container-position-card mobile-container-position-card--grid">
          {position.length ? (
            <MobileVesselPositionGrid
              vessel={vessel}
              containers={[container]}
              focusedContainer={container}
              compact
              direction={direction}
              dockingSide={terminalCall?.dockingSide || vessel?.preferredDockingSide || "port"}
            />
          ) : (
            <Typography className="mobile-empty-message">{t("notPositioned", "Not positioned")}</Typography>
          )}
        </Box>

        <Box className="mobile-container-warning" onClick={onClose}>{t("returnToLoadingList", "Return to loading list")}</Box>

        {/* <Box className="mobile-dialog-actions">
          <Button variant="outlined" startIcon={<EditIcon />}>{t("edit", "Edit")}</Button>
          <Button variant="contained" startIcon={<ManageIcon />} onClick={onManage}>{t("manageContainer", "Manage container")}</Button>
        </Box> */}
      </Box>
    </Dialog>
  );
};

export default MobileContainerDetailsDialog;
